// ProjectsEditable.tsx
import React, { useState, useEffect, useRef } from 'react';
import Select, { type SingleValue } from 'react-select';
// import 'react-select/dist/react-select.css';
import { nanoid } from 'nanoid';
import CorgeeStorageHelper from './CorgeeStorageHelper';
import './ProjectsEditable.css';
import { newTicketService } from '@services/NewTicketService';

// ==================== TYPES ====================

interface Project {
  ProjectId: number;
  ProjectID?: number;
  Name: string;
  [key: string]: any;
}

interface Conversation {
  ConfigValueId: number;
  selectedConfigValueId?: number;
  MilestoneId?: number;
  ProjectId?: number;
  ProjectID?: number;
  ProjectName?: string;
  [key: string]: any;
}

interface Config {
  //   apiUrl: string;
  //   authToken: string;
  enableCmbProject: boolean;
  onLoadDataCallbackProject?: (
    ref: any,
    objects: LoadDataObjects,
    functions: LoadDataFunctions
  ) => void;
  UpdateSelectedProjectReact?: (project: Project | null) => void;
}

interface LoadDataObjects {
  data: Project[];
  selectedProject: Project;
  isNew: boolean;
}

interface LoadDataFunctions {
  // Add any callback functions here if needed
}

interface ProjectsEditableProps {
  config: Config;
  conversation: Conversation;
  isNew?: boolean;
  onChangedProject?: (e: any, conversation: Conversation) => void;
  isForTicket?: boolean;
}

// ==================== MAIN COMPONENT ====================

const ProjectsEditable: React.FC<ProjectsEditableProps> = ({
  config,
  conversation,
  isNew = false,
  onChangedProject: _onChangedProject,
  isForTicket = false,
}) =>
{
  // State management
  const [canEdit, setCanEdit] = useState(false);
  void canEdit;
  const [selectedProject, setSelectedProject] = useState<Project>({
    ProjectId: 0,
    Name: 'Not Set',
  });
  const [selectedProjectName, setSelectedProjectName] = useState('Not Set');
  void selectedProjectName;
  const [dataProjects, setDataProjects] = useState<Project[]>([]);
  const [configValueId, setConfigValueId] = useState(conversation.ConfigValueId);
  const [isLoading, setIsLoading] = useState(false);
  const [previousProjectId, setPreviousProjectId] = useState<number | undefined>();
  void previousProjectId;

  // Refs
  const corgeeProjectsEditableRef = useRef<HTMLDivElement>(null);
  const elementId = useRef(nanoid());

  // Constants
  //   const apiConfig: ApiConfig = {
  //     url: config.apiUrl,
  //     token: config.authToken,
  //   };
  const enableProject = config.enableCmbProject;

  // Get project ID from conversation
  const getProjectIdFromConversation = (conv: Conversation): number =>
  {
    return conv.MilestoneId ?? conv.ProjectId ?? conv.ProjectID ?? 0;
  };

  // Load projects from API
  const getProjects = async () =>
  {
    try
    {
      setIsLoading(true);

      const response: Project[] = await newTicketService.GetProjectsByConfigValueId(configValueId);

      setDataProjects(response);

      // Set selected project
      const projectId = getProjectIdFromConversation(conversation);
      const isNewItemProjectViewBlue =
        !isNaN(projectId) && isNew === true && enableProject === true;

      const newSelectedProject: Project = { ProjectId: 0, Name: 'Not Set' };

      if (isNewItemProjectViewBlue || !isNew || !enableProject)
      {
        newSelectedProject.ProjectId = projectId;

        if (conversation.ProjectName && conversation.ProjectName.length > 0)
        {
          newSelectedProject.Name = conversation.ProjectName;
          setSelectedProjectName(conversation.ProjectName);
        }

        setPreviousProjectId(projectId);
      }

      const canEditValidated = CorgeeStorageHelper.canEdit();
      setCanEdit(canEditValidated);
      setSelectedProject(newSelectedProject);

      // Callback
      if (config.onLoadDataCallbackProject)
      {
        const reactParamsObjects: LoadDataObjects = {
          data: response,
          selectedProject: newSelectedProject,
          isNew: isNew,
        };

        const reactParamsFunctions: LoadDataFunctions = {};

        config.onLoadDataCallbackProject(
          corgeeProjectsEditableRef.current,
          reactParamsObjects,
          reactParamsFunctions,
        );
      }
    }
    catch (error)
    {
      console.warn('Api Error', error);
    }
    finally
    {
      setIsLoading(false);
    }
  };

  // Handle project selection
  const onSelectProject = (selectedItem: SingleValue<Project>) =>
  {
    if (selectedItem)
    {
      setSelectedProject(selectedItem);
      setSelectedProjectName(selectedItem.Name);
    }
    else
    {
      setSelectedProject({ ProjectId: 0, Name: 'Not Set' });
      setSelectedProjectName('Not Set');
    }

    if (config.UpdateSelectedProjectReact)
    {
      config.UpdateSelectedProjectReact(selectedItem);
    }
  };

  // Apply custom styles (legacy jQuery logic - consider removing)
  const _onApplyStyle = () =>
  {
    const containerElement = ($ as any)(corgeeProjectsEditableRef.current);
    const targetElement = containerElement.find(`#select-target-${elementId.current}`);

    if (targetElement.length > 0)
    {
      const selectElement = ($ as any)(`#select-render-${elementId.current}`);
      selectElement
        .find('.Select')
        .first()
        .attr(
          'style',
          `position:fixed; top: ${targetElement.offset()?.top}px; left: ${targetElement.offset()?.left}px`,
        );
    }
  };
  void _onApplyStyle;

  // Initialize on mount
  useEffect(() =>
  {
    if (conversation && !isForTicket)
    {
      getProjects();
    }
    else if (isForTicket)
    {
      // For ticket mode, set up with conversation data
      const ticketProject: Project = {
        ProjectId: conversation.ProjectId ?? 0,
        Name: conversation.ProjectName ?? 'Not Set',
      };

      setDataProjects([ticketProject]);
      setSelectedProjectName(ticketProject.Name);
      setCanEdit(false);
      setSelectedProject(ticketProject);
    }
  }, []);

  // Handle props changes (replaces componentWillReceiveProps)
  useEffect(() =>
  {
    if (!selectedProject || !conversation) return;

    const projectId = getProjectIdFromConversation(conversation);

    if (conversation.selectedConfigValueId)
    {
      setConfigValueId(conversation.selectedConfigValueId);
    }

    if (selectedProject.ProjectId !== projectId)
    {
      const newSelectedProject: Project = {
        ProjectId: projectId,
        Name:
          conversation.ProjectName && conversation.ProjectName.length > 0
            ? conversation.ProjectName
            : 'Not Set',
      };

      setSelectedProject(newSelectedProject);
      setSelectedProjectName(newSelectedProject.Name);
      setPreviousProjectId(projectId);
    }
  }, [conversation, getProjectIdFromConversation]);

  const isDark = document.documentElement.classList.contains('dark');

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      backgroundColor: isDark ? '#3a3c3c' : base.backgroundColor,
      borderColor: isDark ? '#4a4c4c' : base.borderColor,
      boxShadow: 'none',
      '&:hover': { borderColor: isDark ? '#6e6860' : base.borderColor },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: isDark ? '#333535' : base.backgroundColor,
      border: isDark ? '1px solid #4a4c4c' : base.border,
      borderRadius: 0,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: isDark
        ? state.isSelected
          ? '#3c6c80'
          : state.isFocused
            ? '#3a3c3c'
            : '#333535'
        : base.backgroundColor,
      color: isDark ? '#a9a093' : base.color,
      fontSize: '12px',
      padding: '4px 8px',
    }),
    singleValue: (base: any) => ({
      ...base,
      color: isDark ? '#a9a093' : base.color,
      fontSize: '13px',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: isDark ? '#6e6860' : base.color,
    }),
    input: (base: any) => ({
      ...base,
      color: isDark ? '#a9a093' : base.color,
    }),
    indicatorSeparator: (base: any) => ({
      ...base,
      backgroundColor: isDark ? '#4a4c4c' : base.backgroundColor,
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: isDark ? '#6e6860' : base.color,
      '&:hover': { color: isDark ? '#a9a093' : undefined },
    }),
    loadingIndicator: (base: any) => ({
      ...base,
      color: isDark ? '#62accb' : base.color,
    }),
  };

  return (
    <div className="cmbProjectsEditable" ref={corgeeProjectsEditableRef}>
      <Select
        value={selectedProject}
        defaultValue={dataProjects[0]}
        options={dataProjects}
        openMenuOnFocus={true}
        closeMenuOnSelect={true}
        isClearable={false}
        isDisabled={!enableProject}
        autoFocus={true}
        getOptionLabel={option => option.Name}
        getOptionValue={option => String(option.ProjectId)}
        onChange={onSelectProject}
        placeholder="Select a Project..."
        isLoading={isLoading}
        styles={selectStyles}
      />
    </div>
  );
};

export default ProjectsEditable;
